import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Student as StudentIcon, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login({ variant }) {
  // variant can come via prop; fallback to URL path detection
  const location = useLocation();
  const isAdmin = variant === 'admin' || location.pathname.startsWith('/admin');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (isAdmin && data.role !== 'admin') {
        setError('This page is for administrators only. Please use the recruiter login.');
        setLoading(false);
        return;
      }
      if (!isAdmin && data.role === 'admin') {
        // Admin signed in on recruiter page — redirect them to admin dashboard silently
        toast.success('Welcome back, admin!');
        navigate('/admin');
        return;
      }
      toast.success('Welcome back!');
      if (data.must_change_password) navigate('/change-password');
      else if (data.role === 'admin') navigate('/admin');
      else if (data.role === 'institution') navigate('/institution');
      else navigate('/recruiter');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) setError(formatApiErrorDetail(detail));
      else if (err.message?.includes('Network Error')) setError('Unable to connect to server. Please check your internet connection.');
      else setError(`Error: ${err.response?.status || ''} ${err.message || 'Login failed'}`);
    } finally {
      setLoading(false);
    }
  };

  if (isAdmin) {
    // ===== Admin: dark, clean, single-column, no marketing =====
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-900 relative overflow-hidden" data-testid="admin-login-page">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute top-8 left-8 text-slate-300 text-sm tracking-widest uppercase font-medium opacity-60">Study Alliance · Admin</div>
        <div className="w-full max-w-sm relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-800 border border-slate-700 mb-4">
              <ShieldCheck size={22} className="text-amber-400" weight="duotone" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>Admin Portal</h1>
            <p className="text-slate-400 mt-2 text-sm">Restricted access — authorized personnel only</p>
          </div>
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-7">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              {error && (
                <div className="p-3 bg-red-950/50 border border-red-900 rounded-md text-sm text-red-300" data-testid="admin-login-error">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200 text-xs uppercase tracking-wider">Email</Label>
                <Input
                  id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="admin@studyalliance.ca" required data-testid="admin-login-email-input"
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-amber-500 focus-visible:ring-offset-slate-800"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200 text-xs uppercase tracking-wider">Password</Label>
                <Input
                  id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required data-testid="admin-login-password-input"
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-amber-500 focus-visible:ring-offset-slate-800"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 h-11 font-semibold" data-testid="admin-login-submit-btn">
                {loading ? 'Authenticating...' : 'Sign In'}
                {!loading && <ArrowRight size={16} className="ml-2" />}
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">
            Not an admin? <Link to="/login" className="text-slate-300 hover:text-amber-400 underline underline-offset-2" data-testid="admin-login-recruiter-link">Recruiter login</Link>
          </p>
        </div>
      </div>
    );
  }

  // ===== Recruiter: light, warm, welcoming =====
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-amber-50 via-white to-blue-50 relative overflow-hidden" data-testid="recruiter-login-page">
      {/* Soft floating blurs */}
      <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-blue-200/40 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-amber-200 shadow-sm mb-4">
            <StudentIcon size={26} className="text-blue-700" weight="duotone" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>Welcome Back</h1>
          <p className="text-gray-600 mt-2 text-sm">Sign in to your recruiter portal</p>
        </div>
        <div className="bg-white/80 backdrop-blur border border-gray-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="recruiter-login-form">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="recruiter-login-error">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="recruiter-login-email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-700 hover:text-blue-900 hover:underline" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required data-testid="recruiter-login-password-input" className="h-11" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 font-semibold" data-testid="recruiter-login-submit-btn">
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400 uppercase tracking-widest">New here</span></div>
          </div>
          <Link to="/recruiter-signup" data-testid="recruiter-login-signup-link">
            <Button variant="outline" className="w-full h-11 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800">
              Become a Partner Recruiter
            </Button>
          </Link>
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">
          Admin? <Link to="/admin/login" className="text-gray-600 hover:text-blue-700 underline underline-offset-2" data-testid="recruiter-login-admin-link">Admin login</Link>
        </p>
      </div>
    </div>
  );
}
